export type EmailTemplateDefaults = {
  subject: string
  body: string
}

export type SmsTemplateDefaults = {
  body: string
}

export type PhoneTemplateDefaults = {
  opening: string
}

export type LetterTemplateDefaults = {
  title: string
  subtitle: string
  intro: string
  portal_heading: string
}

export type KlebezettelTemplateDefaults = {
  title: string
  subtitle: string
  intro: string
  qr_heading: string
  qr_body: string
}

export type OutreachTemplateDefaults = {
  email: {
    announcement: EmailTemplateDefaults
    confirmation: EmailTemplateDefaults
    cancellation: EmailTemplateDefaults
    reschedule: EmailTemplateDefaults
    follow_up: EmailTemplateDefaults
  }
  sms: {
    announcement: SmsTemplateDefaults
    reschedule: SmsTemplateDefaults
  }
  phone: {
    inbound: PhoneTemplateDefaults
    outbound: PhoneTemplateDefaults
  }
  letter: {
    announcement: {
      personal: LetterTemplateDefaults
      klebezettel: KlebezettelTemplateDefaults
    }
    reschedule: {
      personal: LetterTemplateDefaults
    }
  }
}

export const DEFAULT_LETTER_INTRO =
  "{letter_greeting}\n\n{letter_work_intro_sentence} {appointment_sentence}\n\n{order_type_instruction}\n\n"
export const DEFAULT_RESCHEDULE_LETTER_INTRO =
  "{letter_greeting}\n\n{previous_appointment_sentence}\n\n{letter_work_intro_sentence} {appointment_sentence}\n\n{order_type_instruction}\n\n"
export const DEFAULT_LETTER_TITLE = "Handlung erforderlich: Terminvereinbarung"
export const DEFAULT_LETTER_SUBTITLE = "Arbeiten vom Typ {order_type_label} in {target_unit_phrase}"
export const DEFAULT_LETTER_PORTAL_HEADING =
  "Bitte {response_deadline_phrase} im Portal bestätigen oder verschieben"
export const DEFAULT_KLEBEZETTEL_TITLE = "Wichtige Terminankündigung"
export const DEFAULT_KLEBEZETTEL_SUBTITLE = "Schornsteinfegerarbeiten in Ihrer Wohnung"
const LEGACY_KLEBEZETTEL_WORK_TYPE_INTRO_VARIABLE = "klebezettel_work_type_intro"
const LEGACY_KLEBEZETTEL_WORK_TYPE_INTRO_PLACEHOLDER = `{${LEGACY_KLEBEZETTEL_WORK_TYPE_INTRO_VARIABLE}}`
export const DEFAULT_KLEBEZETTEL_INTRO =
  "Für die Wohnungen in diesem Gebäude stehen folgende Schornsteinfegerarbeiten an:"
export const DEFAULT_KLEBEZETTEL_QR_HEADING = "Termin online verschieben"
export const DEFAULT_KLEBEZETTEL_QR_BODY =
  "Falls der angegebene Zeitraum für Sie nicht passt, öffnen Sie bitte das Terminportal über den QR-Code. Die Chat-Assistenz hilft Ihnen dabei, einen passenden Ersatztermin zu vereinbaren.\n\nDer Vorgang dauert weniger als drei Minuten und funktioniert ohne App und ohne Kundenkonto.\n"
const DEFAULT_MOBILE_MESSAGE_TEMPLATE =
  "Der Kaminkehrer kommt am {mobile_appointment_proposal} fuer {mobile_property_address}. {mobile_appointment_cta}"
const DEFAULT_MOBILE_MESSAGE_RESCHEDULE_TEMPLATE =
  "Ihr Kaminkehrertermin wurde verschoben auf {mobile_appointment_proposal} fuer {mobile_property_address}. {mobile_appointment_cta}"
const LEGACY_CTA_DEFAULT_MOBILE_MESSAGE_TEMPLATE =
  "Der Kaminkehrer kommt am {mobile_appointment_proposal} fuer {mobile_property_address}. Bitte vereinbaren Sie den Termin: {mobile_public_link}"
const LEGACY_CTA_DEFAULT_MOBILE_MESSAGE_RESCHEDULE_TEMPLATE =
  "Ihr Kaminkehrertermin wurde verschoben auf {mobile_appointment_proposal} fuer {mobile_property_address}. Bitte vereinbaren Sie den Termin: {mobile_public_link}"
const LEGACY_COMPACT_DEFAULT_MOBILE_MESSAGE_TEMPLATE =
  "Zeitfenster {mobile_appointment_proposal}: {mobile_public_link}"
const LEGACY_WINDOW_ADDRESS_DEFAULT_MOBILE_MESSAGE_TEMPLATE =
  "Zeitfenster {mobile_appointment_proposal} fuer {mobile_property_address}. Bitte vereinbaren: {mobile_public_link}"
const LEGACY_UMLAUT_DEFAULT_MOBILE_MESSAGE_TEMPLATE =
  "Der Kaminkehrer kommt am {mobile_appointment_proposal} fuer {mobile_property_address}. Bitte bestätigen Sie den Termin: {mobile_public_link}"
const LEGACY_DEFAULT_MOBILE_MESSAGE_TEMPLATE =
  "Termin {mobile_appointment_proposal} fuer {mobile_property_address}: {mobile_public_link}"
const LEGACY_SMS_DEFAULT_MOBILE_MESSAGE_TEMPLATE =
  "{organization_name}: Schornsteinfegertermin für {property_address_no_country} abstimmen: {public_link}"
const KNOWN_DEFAULT_MOBILE_MESSAGE_ANNOUNCEMENT_TEMPLATES = [
  DEFAULT_MOBILE_MESSAGE_TEMPLATE,
  LEGACY_CTA_DEFAULT_MOBILE_MESSAGE_TEMPLATE,
  LEGACY_COMPACT_DEFAULT_MOBILE_MESSAGE_TEMPLATE,
  LEGACY_WINDOW_ADDRESS_DEFAULT_MOBILE_MESSAGE_TEMPLATE,
  LEGACY_UMLAUT_DEFAULT_MOBILE_MESSAGE_TEMPLATE,
  LEGACY_DEFAULT_MOBILE_MESSAGE_TEMPLATE,
  LEGACY_SMS_DEFAULT_MOBILE_MESSAGE_TEMPLATE,
]
const KNOWN_DEFAULT_MOBILE_MESSAGE_RESCHEDULE_TEMPLATES = [
  DEFAULT_MOBILE_MESSAGE_RESCHEDULE_TEMPLATE,
  LEGACY_CTA_DEFAULT_MOBILE_MESSAGE_RESCHEDULE_TEMPLATE,
]

export const EMPTY_OUTREACH_TEMPLATE_DEFAULTS: OutreachTemplateDefaults = {
  email: {
    announcement: { subject: "", body: "" },
    confirmation: { subject: "", body: "" },
    cancellation: { subject: "", body: "" },
    reschedule: { subject: "", body: "" },
    follow_up: { subject: "", body: "" },
  },
  sms: {
    announcement: { body: "" },
    reschedule: { body: "" },
  },
  phone: {
    inbound: { opening: "" },
    outbound: { opening: "" },
  },
  letter: {
    announcement: {
      personal: {
        title: DEFAULT_LETTER_TITLE,
        subtitle: DEFAULT_LETTER_SUBTITLE,
        intro: DEFAULT_LETTER_INTRO,
        portal_heading: DEFAULT_LETTER_PORTAL_HEADING,
      },
      klebezettel: {
        title: DEFAULT_KLEBEZETTEL_TITLE,
        subtitle: DEFAULT_KLEBEZETTEL_SUBTITLE,
        intro: DEFAULT_KLEBEZETTEL_INTRO,
        qr_heading: DEFAULT_KLEBEZETTEL_QR_HEADING,
        qr_body: DEFAULT_KLEBEZETTEL_QR_BODY,
      },
    },
    reschedule: {
      personal: {
        title: DEFAULT_LETTER_TITLE,
        subtitle: DEFAULT_LETTER_SUBTITLE,
        intro: DEFAULT_RESCHEDULE_LETTER_INTRO,
        portal_heading: DEFAULT_LETTER_PORTAL_HEADING,
      },
    },
  },
}

const normalizeTemplateValue = (value: unknown) => {
  const text = value === null || value === undefined ? "" : String(value)

  return text.trim()
}

const asRecord = (value: unknown): Record<string, any> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, any>) : {}

const templateVariablePattern = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}|\{\s*([a-zA-Z0-9_]+)\s*\}/g
const normalizeForComparison = (value: unknown) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()

const isKnownTemplate = (value: unknown, templates: string[]) => {
  const normalized = normalizeForComparison(value)

  return (
    Boolean(normalized) &&
    templates.some((template) => normalizeForComparison(template) === normalized)
  )
}

const canonicalMobileTemplateBody = (value: string, kind: "announcement" | "reschedule") => {
  if (
    kind === "reschedule" &&
    isKnownTemplate(value, KNOWN_DEFAULT_MOBILE_MESSAGE_RESCHEDULE_TEMPLATES)
  ) {
    return DEFAULT_MOBILE_MESSAGE_RESCHEDULE_TEMPLATE
  }

  if (
    kind === "announcement" &&
    isKnownTemplate(value, KNOWN_DEFAULT_MOBILE_MESSAGE_ANNOUNCEMENT_TEMPLATES)
  ) {
    return DEFAULT_MOBILE_MESSAGE_TEMPLATE
  }

  return value
}

const templateUsesVariable = (value: string, variableName: string) =>
  Array.from(value.matchAll(templateVariablePattern)).some(
    (match) => String(match[1] || match[2] || "").trim() === variableName,
  )

const canonicalizeTemplateVariable = (value: string, variableName: string) =>
  value.replace(templateVariablePattern, (match, doubleBraceName, singleBraceName) =>
    String(doubleBraceName || singleBraceName || "").trim() === variableName
      ? `{${variableName}}`
      : match,
  )

const LEGACY_KLEBEZETTEL_WORK_TYPE_SENTENCES = [
  "Für dieses Gebäude stehen Schornsteinfegerarbeiten an.",
  "Für die Wohnungen in diesem Gebäude stehen Schornsteinfegerarbeiten an.",
  "Für die Wohnungen in diesem Gebäude stehen folgende Schornsteinfegerarbeiten an:",
]
const LEGACY_KLEBEZETTEL_GREETING_PARAGRAPHS = new Set(["Guten Tag", "Guten Tag,"])
const legacyKlebezettelThanksPattern = /(?:\s*\n\s*)*Vielen Dank!?\s*$/i

const splitTemplateParagraphs = (value: string) =>
  normalizeTemplateValue(value)
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)

const legacyWorkTypeSentenceMatch = (value: string) => {
  const normalized = normalizeForComparison(value)

  return LEGACY_KLEBEZETTEL_WORK_TYPE_SENTENCES.find((sentence) => {
    const canonicalSentence = normalizeForComparison(sentence)

    return normalized === canonicalSentence || normalized.startsWith(`${canonicalSentence} `)
  })
}

const stripLegacyKlebezettelWorkTypeSentence = (value: string) => {
  const sentence = legacyWorkTypeSentenceMatch(value)

  if (!sentence) return value.trim()

  const normalized = normalizeForComparison(value)
  const canonicalSentence = normalizeForComparison(sentence)

  return normalized.slice(canonicalSentence.length).trim()
}

const stripLegacyKlebezettelGreetingParagraphs = (value: string) =>
  splitTemplateParagraphs(value)
    .filter((paragraph) => !LEGACY_KLEBEZETTEL_GREETING_PARAGRAPHS.has(paragraph))
    .join("\n\n")

const stripLegacyKlebezettelThanks = (value: string) =>
  normalizeTemplateValue(value).replace(legacyKlebezettelThanksPattern, "").trim()

const canonicalKlebezettelIntro = (value: unknown) =>
  stripLegacyKlebezettelGreetingParagraphs(normalizeTemplateValue(value)) ||
  DEFAULT_KLEBEZETTEL_INTRO

const canonicalKlebezettelQrBody = (value: unknown) =>
  stripLegacyKlebezettelThanks(normalizeTemplateValue(value)) || DEFAULT_KLEBEZETTEL_QR_BODY

const resolveLetterTemplateCopy = (
  letterCfg: Record<string, any>,
  nestedKey: "personal",
  defaultIntro: string,
): LetterTemplateDefaults => {
  const nested = asRecord(letterCfg[nestedKey] ?? letterCfg[nestedKey.toUpperCase()])

  return {
    title: normalizeTemplateValue(nested.title) || DEFAULT_LETTER_TITLE,
    subtitle: normalizeTemplateValue(nested.subtitle) || DEFAULT_LETTER_SUBTITLE,
    intro: normalizeTemplateValue(nested.intro) || defaultIntro,
    portal_heading: normalizeTemplateValue(nested.portal_heading) || DEFAULT_LETTER_PORTAL_HEADING,
  }
}

const introFromLegacyPrefix = (prefix: string) => {
  const paragraphs = splitTemplateParagraphs(prefix)
    .map((paragraph) => stripLegacyKlebezettelWorkTypeSentence(paragraph))
    .filter(Boolean)

  return canonicalKlebezettelIntro(paragraphs.join("\n\n"))
}

const splitLegacyKlebezettelTemplate = (value: unknown): KlebezettelTemplateDefaults => {
  const raw = normalizeTemplateValue(value)

  if (!raw) {
    return {
      title: DEFAULT_KLEBEZETTEL_TITLE,
      subtitle: DEFAULT_KLEBEZETTEL_SUBTITLE,
      intro: DEFAULT_KLEBEZETTEL_INTRO,
      qr_heading: DEFAULT_KLEBEZETTEL_QR_HEADING,
      qr_body: DEFAULT_KLEBEZETTEL_QR_BODY,
    }
  }

  const canonical = canonicalizeTemplateVariable(raw, LEGACY_KLEBEZETTEL_WORK_TYPE_INTRO_VARIABLE)

  if (templateUsesVariable(canonical, LEGACY_KLEBEZETTEL_WORK_TYPE_INTRO_VARIABLE)) {
    const [prefix, ...suffixParts] = canonical.split(LEGACY_KLEBEZETTEL_WORK_TYPE_INTRO_PLACEHOLDER)

    return {
      title: DEFAULT_KLEBEZETTEL_TITLE,
      subtitle: DEFAULT_KLEBEZETTEL_SUBTITLE,
      intro: introFromLegacyPrefix(prefix || ""),
      qr_heading: DEFAULT_KLEBEZETTEL_QR_HEADING,
      qr_body: canonicalKlebezettelQrBody(suffixParts.join("")),
    }
  }

  const paragraphs = splitTemplateParagraphs(canonical)
  const sentenceIndex = paragraphs.findIndex((paragraph) =>
    Boolean(legacyWorkTypeSentenceMatch(paragraph)),
  )

  if (sentenceIndex >= 0) {
    const bodyParagraphs = [
      stripLegacyKlebezettelWorkTypeSentence(paragraphs[sentenceIndex] || ""),
      ...paragraphs.slice(sentenceIndex + 1),
    ].filter(Boolean)

    return {
      title: DEFAULT_KLEBEZETTEL_TITLE,
      subtitle: DEFAULT_KLEBEZETTEL_SUBTITLE,
      intro: introFromLegacyPrefix(paragraphs.slice(0, sentenceIndex).join("\n\n")),
      qr_heading: DEFAULT_KLEBEZETTEL_QR_HEADING,
      qr_body: canonicalKlebezettelQrBody(bodyParagraphs.join("\n\n")),
    }
  }

  return {
    title: DEFAULT_KLEBEZETTEL_TITLE,
    subtitle: DEFAULT_KLEBEZETTEL_SUBTITLE,
    intro: DEFAULT_KLEBEZETTEL_INTRO,
    qr_heading: DEFAULT_KLEBEZETTEL_QR_HEADING,
    qr_body: canonicalKlebezettelQrBody(canonical),
  }
}

export const normalizeKlebezettelIntroTemplate = (value: unknown) =>
  canonicalKlebezettelIntro(value)

export const normalizeKlebezettelQrBodyTemplate = (value: unknown) =>
  splitLegacyKlebezettelTemplate(value).qr_body

const nestedValue = (
  source: Record<string, any>,
  nestedKey: string,
  field: string,
  legacyFields: string[] = [],
) => {
  const nested = asRecord(source[nestedKey] ?? source[nestedKey.toUpperCase()])
  for (const legacyField of legacyFields) {
    if (nested[field] !== undefined) break

    if (source[legacyField] !== undefined) return normalizeTemplateValue(source[legacyField])
  }

  return normalizeTemplateValue(nested[field])
}

export const resolveTemplateDefaults = (
  templates?: Record<string, any> | null,
): OutreachTemplateDefaults => {
  const rawTemplates = asRecord(templates)
  const pick = (key: string) => asRecord(rawTemplates[key] ?? rawTemplates[key.toUpperCase()])

  const emailCfg = pick("email")
  const smsCfg = pick("sms")
  const phoneCfg = pick("phone")
  const letterCfg = pick("letter")
  const hasAnnouncementLetterBranch =
    letterCfg.announcement !== undefined || letterCfg.ANNOUNCEMENT !== undefined
  const announcementLetterCfg = hasAnnouncementLetterBranch
    ? asRecord(letterCfg.announcement ?? letterCfg.ANNOUNCEMENT)
    : letterCfg
  const rescheduleLetterCfg = asRecord(letterCfg.reschedule ?? letterCfg.RESCHEDULE)

  const legacyEmailSubject = normalizeTemplateValue(emailCfg.subject)
  const legacyEmailBody = normalizeTemplateValue(emailCfg.body)
  const klebezettelCfg = asRecord(
    announcementLetterCfg.klebezettel ?? announcementLetterCfg.KLEBEZETTEL,
  )
  const rawKlebezettelIntro =
    klebezettelCfg.intro !== undefined
      ? klebezettelCfg.intro
      : announcementLetterCfg.klebezettel_intro
  const rawKlebezettelBody =
    klebezettelCfg.qr_body !== undefined
      ? klebezettelCfg.qr_body
      : klebezettelCfg.body !== undefined
        ? klebezettelCfg.body
        : announcementLetterCfg.klebezettel_body
  const rawKlebezettelQrHeading =
    klebezettelCfg.qr_heading !== undefined
      ? klebezettelCfg.qr_heading
      : announcementLetterCfg.klebezettel_qr_heading
  const legacyKlebezettel = splitLegacyKlebezettelTemplate(rawKlebezettelBody)

  return {
    email: {
      announcement: {
        subject: nestedValue(emailCfg, "announcement", "subject", ["subject"]),
        body: nestedValue(emailCfg, "announcement", "body", ["body"]),
      },
      confirmation: {
        subject: nestedValue(emailCfg, "confirmation", "subject") || legacyEmailSubject,
        body: nestedValue(emailCfg, "confirmation", "body") || legacyEmailBody,
      },
      cancellation: {
        subject: nestedValue(emailCfg, "cancellation", "subject") || legacyEmailSubject,
        body: nestedValue(emailCfg, "cancellation", "body"),
      },
      reschedule: {
        subject: nestedValue(emailCfg, "reschedule", "subject") || legacyEmailSubject,
        body: nestedValue(emailCfg, "reschedule", "body") || legacyEmailBody,
      },
      follow_up: {
        subject: nestedValue(emailCfg, "follow_up", "subject") || legacyEmailSubject,
        body: nestedValue(emailCfg, "follow_up", "body") || legacyEmailBody,
      },
    },
    sms: {
      announcement: {
        body: canonicalMobileTemplateBody(
          nestedValue(smsCfg, "announcement", "body", ["body"]),
          "announcement",
        ),
      },
      reschedule: {
        body: canonicalMobileTemplateBody(nestedValue(smsCfg, "reschedule", "body"), "reschedule"),
      },
    },
    phone: {
      inbound: {
        opening: nestedValue(phoneCfg, "inbound", "opening", ["inbound_opening"]),
      },
      outbound: {
        opening: nestedValue(phoneCfg, "outbound", "opening", ["outbound_opening"]),
      },
    },
    letter: {
      announcement: {
        personal: resolveLetterTemplateCopy(
          announcementLetterCfg,
          "personal",
          DEFAULT_LETTER_INTRO,
        ),
        klebezettel: {
          title:
            nestedValue(announcementLetterCfg, "klebezettel", "title", ["klebezettel_title"]) ||
            DEFAULT_KLEBEZETTEL_TITLE,
          subtitle:
            nestedValue(announcementLetterCfg, "klebezettel", "subtitle", [
              "klebezettel_subtitle",
            ]) || DEFAULT_KLEBEZETTEL_SUBTITLE,
          intro:
            rawKlebezettelIntro !== undefined
              ? normalizeKlebezettelIntroTemplate(rawKlebezettelIntro)
              : legacyKlebezettel.intro,
          qr_heading:
            normalizeTemplateValue(rawKlebezettelQrHeading) || legacyKlebezettel.qr_heading,
          qr_body:
            rawKlebezettelIntro !== undefined
              ? normalizeKlebezettelQrBodyTemplate(rawKlebezettelBody)
              : legacyKlebezettel.qr_body,
        },
      },
      reschedule: {
        personal: resolveLetterTemplateCopy(
          rescheduleLetterCfg,
          "personal",
          DEFAULT_RESCHEDULE_LETTER_INTRO,
        ),
      },
    },
  }
}
